import {
  AlertTriangle,
  ArrowDownToLine,
  Filter,
  Pause,
  Play,
  RefreshCw,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { usePlatformApi } from "@/hooks/usePlatformApi";
import type {
  OperationalLogAggregateBucket,
  OperationalLogFilter,
  OperationalLogLevel,
  OperationalLogRecord,
} from "@/types/operational-log";

/**
 * Top-level operational-logs viewer (operational-logging-plan §4.1).
 *
 * Two consumption surfaces converge here:
 *
 *   1. **Direct nav from the Platform sidebar** → no preset filter,
 *      operator picks slice (service / level / time / free-text).
 *
 *   2. **Per-runtime deep-link** (`§4.2`) → URL carries
 *      `?container_owner_user_id=<userId>` (NOT `service=…` — both
 *      `lexiun-runtime` and `lexiun-harn-service` emit from the same
 *      container, so service-pinning would hide half the records).
 *      The query-string-driven filter is the single source of truth —
 *      a single page, two entry-points, bookmarkable filter state.
 *
 * **Selection deep-link**: `/operational-logs/:logId` opens the page
 * with the detail drawer pre-populated. Mirrors the FeedbackBrowser /
 * ActivityBrowser convention — record-level URLs are shareable.
 *
 * Backed by `GET /api/platform/logs[/aggregate|/stream|/:id]` which
 * lexserver Phase 1.7 ships. The live-tail uses fetch+ReadableStream
 * (not native EventSource) so the JWT can travel in `Authorization`.
 */

const PAGE_SIZE = 100;
const TIMEFRAME_OPTIONS = [
  { value: 1, label: "Last 1 hour" },
  { value: 6, label: "Last 6 hours" },
  { value: 24, label: "Last 24 hours" },
  { value: 72, label: "Last 3 days" },
  { value: 168, label: "Last 7 days" },
] as const;

const ALL_LEVELS: OperationalLogLevel[] = [
  "trace",
  "debug",
  "info",
  "warn",
  "error",
];

const LEVEL_COLOR: Record<OperationalLogLevel, string> = {
  trace: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
  debug: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  info: "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300",
  warn: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
  error: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300",
};

interface UiFilter {
  service: string;
  /** Multi-select; empty array = no level filter. */
  levels: OperationalLogLevel[];
  user_id: string;
  org_id: string;
  container_owner_user_id: string;
  subsystem: string;
  trace_id: string;
  q: string;
  /** Hours back from now. */
  timeframeHours: number;
}

const EMPTY_FILTER: UiFilter = {
  service: "",
  levels: [],
  user_id: "",
  org_id: "",
  container_owner_user_id: "",
  subsystem: "",
  trace_id: "",
  q: "",
  timeframeHours: 1,
};

function uiFilterFromSearchParams(
  searchParams: URLSearchParams,
): UiFilter {
  const levelsRaw = searchParams.get("level");
  const levels = levelsRaw
    ? (levelsRaw
        .split(",")
        .filter((v): v is OperationalLogLevel =>
          (ALL_LEVELS as string[]).includes(v),
        ) as OperationalLogLevel[])
    : [];
  const tf = Number(searchParams.get("timeframe") ?? "1");
  return {
    service: searchParams.get("service") ?? "",
    levels,
    user_id: searchParams.get("user_id") ?? "",
    org_id: searchParams.get("org_id") ?? "",
    container_owner_user_id:
      searchParams.get("container_owner_user_id") ?? "",
    subsystem: searchParams.get("subsystem") ?? "",
    trace_id: searchParams.get("trace_id") ?? "",
    q: searchParams.get("q") ?? "",
    timeframeHours: Number.isFinite(tf) && tf > 0 ? tf : 1,
  };
}

function uiFilterToServerFilter(f: UiFilter): OperationalLogFilter {
  const out: OperationalLogFilter = {};
  if (f.service) out.service = f.service;
  if (f.levels.length > 0) out.level = f.levels;
  if (f.user_id) out.user_id = f.user_id;
  if (f.org_id) out.org_id = f.org_id;
  if (f.container_owner_user_id)
    out.container_owner_user_id = f.container_owner_user_id;
  if (f.subsystem) out.subsystem = f.subsystem;
  if (f.trace_id) out.trace_id = f.trace_id;
  if (f.q) out.q = f.q;
  out.from = new Date(Date.now() - f.timeframeHours * 3_600_000).toISOString();
  return out;
}

function formatTime(ts: number): string {
  const d = new Date(ts);
  const date = d.toLocaleDateString(undefined, {
    month: "short",
    day: "2-digit",
  });
  const time = d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  return `${date} ${time}`;
}

export function OperationalLogs() {
  const api = usePlatformApi();
  const navigate = useNavigate();
  // **Medium #6**: URL is the source of truth for selection too —
  // /operational-logs/:logId opens the detail drawer. Matches the
  // FeedbackBrowser / ActivityBrowser convention.
  const { logId: urlLogId } = useParams<{ logId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = useMemo(
    () => uiFilterFromSearchParams(searchParams),
    [searchParams],
  );

  const [records, setRecords] = useState<OperationalLogRecord[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<OperationalLogRecord | null>(null);
  const [aggregate, setAggregate] = useState<OperationalLogAggregateBucket[]>(
    [],
  );
  const [liveTail, setLiveTail] = useState(false);
  const liveCloseRef = useRef<(() => void) | null>(null);

  const setSelectedId = useCallback(
    (id: string | null) => {
      // Preserve current filter query string when navigating to/from
      // the record-level URL — operators want the page to "remember"
      // what they were looking at when they close the drawer.
      const qs = searchParams.toString();
      const suffix = qs ? `?${qs}` : "";
      navigate(id ? `/operational-logs/${encodeURIComponent(id)}${suffix}` : `/operational-logs${suffix}`);
    },
    [navigate, searchParams],
  );

  // ── Page list loader ──────────────────────────────────────────────
  const load = useCallback(
    async (append: boolean, currentCursor?: string) => {
      setLoading(true);
      setError(null);
      try {
        const serverFilter = uiFilterToServerFilter(filter);
        const res = await api.listOperationalLogs({
          ...serverFilter,
          limit: PAGE_SIZE,
          cursor: currentCursor,
        });
        setRecords((prev) =>
          append ? [...prev, ...res.data.records] : res.data.records,
        );
        setCursor(res.data.nextCursor);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    },
    [api, filter],
  );

  // ── Aggregate (sparkline) loader ──────────────────────────────────
  const loadAggregate = useCallback(async () => {
    try {
      const res = await api.aggregateOperationalLogs(
        uiFilterToServerFilter(filter),
      );
      setAggregate(res.data.buckets);
    } catch {
      setAggregate([]);
    }
  }, [api, filter]);

  // Reload list + aggregate any time the filter changes.
  useEffect(() => {
    setCursor(undefined);
    void load(false, undefined);
    void loadAggregate();
  }, [load, loadAggregate]);

  // **Medium #6**: sync selection state to the URL `:logId` segment.
  // Path takes the in-memory record when present (avoids a round-trip
  // for normal click-row-from-list flows); falls back to
  // `getOperationalLog(id)` when the URL hits a record outside the
  // current page (deep-link / bookmark / "view all logs for trace_id"
  // filtered out the original record).
  useEffect(() => {
    if (!urlLogId) {
      setSelected(null);
      return;
    }
    const found = records.find((r) => r._id === urlLogId);
    if (found) {
      setSelected(found);
      return;
    }
    let cancelled = false;
    void api
      .getOperationalLog(urlLogId)
      .then((res) => {
        if (!cancelled) setSelected(res.data);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
          setSelected(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, urlLogId, records]);

  // ── Live tail ─────────────────────────────────────────────────────
  // Toggling on opens a streaming subscription scoped to the SAME
  // server filter. New records are prepended (most-recent-first matches
  // the table ordering). On unmount or toggle-off the subscription is
  // closed; aborting the underlying fetch unwinds the server-side
  // ReadableStream's polling loop within one poll interval.
  useEffect(() => {
    if (!liveTail) {
      liveCloseRef.current?.();
      liveCloseRef.current = null;
      return;
    }
    let cancelled = false;
    void api
      .streamOperationalLogs(
        uiFilterToServerFilter(filter),
        (rec) => {
          if (cancelled) return;
          setRecords((prev) => {
            // De-dup on _id in case the same record arrives twice on a
            // poll boundary (last-seen-since rounding).
            if (rec._id && prev.some((r) => r._id === rec._id)) return prev;
            return [rec, ...prev].slice(0, 500);
          });
        },
        (err) => {
          if (cancelled) return;
          setError(err.message);
        },
      )
      .then((sub) => {
        if (cancelled) {
          sub.close();
          return;
        }
        liveCloseRef.current = sub.close;
      });
    return () => {
      cancelled = true;
      liveCloseRef.current?.();
      liveCloseRef.current = null;
    };
  }, [api, filter, liveTail]);

  // ── Filter-mutator helpers (URL-bound) ────────────────────────────
  const updateFilter = useCallback(
    (patch: Partial<UiFilter>) => {
      const next = { ...filter, ...patch };
      const sp = new URLSearchParams();
      if (next.service) sp.set("service", next.service);
      if (next.levels.length > 0) sp.set("level", next.levels.join(","));
      if (next.user_id) sp.set("user_id", next.user_id);
      if (next.org_id) sp.set("org_id", next.org_id);
      if (next.container_owner_user_id)
        sp.set("container_owner_user_id", next.container_owner_user_id);
      if (next.subsystem) sp.set("subsystem", next.subsystem);
      if (next.trace_id) sp.set("trace_id", next.trace_id);
      if (next.q) sp.set("q", next.q);
      if (next.timeframeHours !== EMPTY_FILTER.timeframeHours)
        sp.set("timeframe", String(next.timeframeHours));
      setSearchParams(sp, { replace: true });
    },
    [filter, setSearchParams],
  );

  const clearAll = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  const toggleLevel = (level: OperationalLogLevel) => {
    updateFilter({
      levels: filter.levels.includes(level)
        ? filter.levels.filter((l) => l !== level)
        : [...filter.levels, level],
    });
  };

  // ── Aggregate roll-up by level (for the sparkline summary card) ──
  const levelTotals = useMemo(() => {
    const totals: Record<string, number> = { trace: 0, debug: 0, info: 0, warn: 0, error: 0 };
    for (const b of aggregate) {
      totals[b.level] = (totals[b.level] ?? 0) + b.count;
    }
    return totals;
  }, [aggregate]);

  return (
    <div className="flex h-full flex-col gap-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Operational Logs</h1>
        <div className="flex items-center gap-2">
          <select
            value={filter.timeframeHours}
            onChange={(e) =>
              updateFilter({ timeframeHours: Number(e.target.value) })
            }
            className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900"
          >
            {TIMEFRAME_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setLiveTail((v) => !v)}
            className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
              liveTail
                ? "border-indigo-500 bg-indigo-50 text-indigo-700 dark:border-indigo-400 dark:bg-indigo-950 dark:text-indigo-300"
                : "border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"
            }`}
            title={liveTail ? "Pause live tail" : "Start live tail"}
          >
            {liveTail ? (
              <Pause className="h-3.5 w-3.5" />
            ) : (
              <Play className="h-3.5 w-3.5" />
            )}
            {liveTail ? "Live" : "Tail"}
          </button>
          <button
            type="button"
            onClick={() => {
              setCursor(undefined);
              void load(false, undefined);
              void loadAggregate();
            }}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-sm font-medium hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"
            title="Refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Aggregate summary card */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Filter className="h-4 w-4" /> Counts by level (current filter)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            {ALL_LEVELS.map((lvl) => (
              <button
                key={lvl}
                type="button"
                onClick={() => toggleLevel(lvl)}
                className={`flex items-center gap-2 rounded-md border px-3 py-1.5 transition-colors ${
                  filter.levels.includes(lvl)
                    ? "border-indigo-500 bg-indigo-50 dark:border-indigo-400 dark:bg-indigo-950"
                    : "border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"
                }`}
              >
                <span
                  className={`rounded px-1.5 py-0.5 font-mono text-[10px] uppercase ${LEVEL_COLOR[lvl]}`}
                >
                  {lvl}
                </span>
                <span className="font-mono tabular-nums">
                  {levelTotals[lvl] ?? 0}
                </span>
              </button>
            ))}
            {(filter.service ||
              filter.user_id ||
              filter.org_id ||
              filter.container_owner_user_id ||
              filter.subsystem ||
              filter.trace_id ||
              filter.q ||
              filter.levels.length > 0) && (
              <button
                type="button"
                onClick={clearAll}
                className="ml-auto inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"
              >
                <X className="h-3 w-3" /> Clear filters
              </button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Filter row */}
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        <FilterField
          placeholder="service (e.g. lexiun-server)"
          value={filter.service}
          onChange={(v) => updateFilter({ service: v })}
        />
        <FilterField
          placeholder="subsystem"
          value={filter.subsystem}
          onChange={(v) => updateFilter({ subsystem: v })}
        />
        <FilterField
          placeholder="user_id"
          value={filter.user_id}
          onChange={(v) => updateFilter({ user_id: v })}
        />
        <FilterField
          placeholder="org_id"
          value={filter.org_id}
          onChange={(v) => updateFilter({ org_id: v })}
        />
        <FilterField
          placeholder="container_owner_user_id"
          value={filter.container_owner_user_id}
          onChange={(v) => updateFilter({ container_owner_user_id: v })}
        />
        <FilterField
          placeholder="trace_id"
          value={filter.trace_id}
          onChange={(v) => updateFilter({ trace_id: v })}
        />
        <FilterField
          placeholder="search message…"
          value={filter.q}
          onChange={(v) => updateFilter({ q: v })}
          colSpan={2}
        />
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Main table + detail drawer */}
      <div className="flex flex-1 gap-4 overflow-hidden">
        <div className="flex-1 overflow-auto rounded-md border border-slate-200 dark:border-slate-800">
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 bg-slate-50 dark:bg-slate-900">
              <tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 dark:border-slate-700">
                <th className="px-3 py-2">Time</th>
                <th className="px-3 py-2">Level</th>
                <th className="px-3 py-2">Service</th>
                <th className="px-3 py-2">Subsystem</th>
                <th className="px-3 py-2">Message</th>
              </tr>
            </thead>
            <tbody>
              {records.length === 0 && !loading && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-3 py-12 text-center text-sm text-slate-400"
                  >
                    No records match the current filter.
                  </td>
                </tr>
              )}
              {records.map((r, idx) => (
                <tr
                  key={r._id ?? `${r.timestamp_ms}-${idx}`}
                  onClick={() => r._id && setSelectedId(r._id)}
                  className={`cursor-pointer border-b border-slate-100 hover:bg-slate-50 dark:border-slate-900 dark:hover:bg-slate-900/50 ${
                    selected?._id && selected._id === r._id
                      ? "bg-indigo-50 dark:bg-indigo-950/30"
                      : ""
                  }`}
                >
                  <td className="whitespace-nowrap px-3 py-1.5 font-mono text-xs text-slate-500">
                    {formatTime(r.timestamp_ms)}
                  </td>
                  <td className="px-3 py-1.5">
                    <span
                      className={`rounded px-1.5 py-0.5 font-mono text-[10px] uppercase ${LEVEL_COLOR[r.level]}`}
                    >
                      {r.level}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 font-mono text-xs text-slate-600 dark:text-slate-400">
                    {r.service}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-xs text-slate-500">
                    {r.subsystem ?? ""}
                  </td>
                  <td className="px-3 py-1.5">
                    <span className="line-clamp-1 break-all">{r.message}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {cursor && !liveTail && (
            <div className="border-t border-slate-200 p-3 text-center dark:border-slate-800">
              <button
                type="button"
                onClick={() => void load(true, cursor)}
                disabled={loading}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-sm font-medium hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:hover:bg-slate-800"
              >
                <ArrowDownToLine className="h-3.5 w-3.5" /> Load more
              </button>
            </div>
          )}
        </div>

        {/* Detail drawer */}
        {selected && (
          <DetailDrawer
            record={selected}
            onClose={() => setSelectedId(null)}
            onFilterByTrace={(traceId) =>
              updateFilter({ trace_id: traceId })
            }
          />
        )}
      </div>
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────

/**
 * Debounced text input. The committed value (`value` prop) drives
 * which records the page fetches; the local `inputValue` state lets
 * the user type without firing a request per keystroke. The
 * 300ms idle timer commits via `onChange` once the user stops typing.
 *
 * Sync rule: when the URL-driven `value` changes (e.g. clear-filters
 * button, browser back, deep-link), reset the local input so the
 * field reflects the source of truth. Use a ref+effect rather than
 * `value` as a `useState` initialiser because the latter only fires
 * once at mount.
 */
function FilterField({
  placeholder,
  value,
  onChange,
  colSpan,
  debounceMs = 300,
}: {
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  colSpan?: number;
  debounceMs?: number;
}) {
  const [inputValue, setInputValue] = useState(value);
  // External resets (clear-filters, deep-link) → mirror into local.
  useEffect(() => {
    setInputValue(value);
  }, [value]);
  // Debounced commit. The cleanup tears down a stale timer on every
  // keystroke, so the commit only fires when the user idles for
  // `debounceMs`.
  useEffect(() => {
    if (inputValue === value) return;
    const handle = setTimeout(() => onChange(inputValue), debounceMs);
    return () => clearTimeout(handle);
  }, [inputValue, value, onChange, debounceMs]);
  return (
    <input
      type="text"
      placeholder={placeholder}
      value={inputValue}
      onChange={(e) => setInputValue(e.target.value)}
      onKeyDown={(e) => {
        // Enter commits immediately — gives operators a "search now"
        // affordance without waiting for the debounce.
        if (e.key === "Enter" && inputValue !== value) onChange(inputValue);
      }}
      className={`rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900 ${colSpan === 2 ? "lg:col-span-2" : ""}`}
    />
  );
}

function DetailDrawer({
  record,
  onClose,
  onFilterByTrace,
}: {
  record: OperationalLogRecord;
  onClose: () => void;
  onFilterByTrace: (traceId: string) => void;
}) {
  return (
    <div className="flex w-[420px] shrink-0 flex-col overflow-hidden rounded-md border border-slate-200 dark:border-slate-800">
      <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-900">
        <h3 className="text-sm font-semibold">Record detail</h3>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 hover:bg-slate-200 dark:hover:bg-slate-700"
          aria-label="Close detail"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="space-y-3 overflow-auto p-3 text-sm">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500">
            Message
          </div>
          <div className="mt-1 break-words font-mono text-xs">
            {record.message}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <KV label="Level" value={record.level} mono />
          <KV label="Service" value={record.service} mono />
          <KV label="Subsystem" value={record.subsystem} mono />
          <KV label="Source" value={record.source} mono />
          <KV
            label="Timestamp"
            value={`${formatTime(record.timestamp_ms)} (${record.timestamp_ms})`}
          />
          {record.trace_id && (
            <div className="col-span-2 flex items-center justify-between gap-2 rounded bg-slate-50 px-2 py-1 dark:bg-slate-900">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">
                  Trace
                </div>
                <div className="truncate font-mono text-[11px]">
                  {record.trace_id}
                </div>
              </div>
              <button
                type="button"
                onClick={() => onFilterByTrace(record.trace_id!)}
                className="shrink-0 rounded-md border border-slate-200 px-2 py-1 text-[11px] hover:bg-slate-100 dark:border-slate-700 dark:hover:bg-slate-800"
              >
                Filter by trace_id
              </button>
            </div>
          )}
          <KV label="Span" value={record.span_id} mono />
          <KV label="Parent span" value={record.parent_span_id} mono />
          <KV
            label="Container user"
            value={record.container_owner_user_id}
            mono
          />
          <KV
            label="Container org"
            value={record.container_owner_org_id}
            mono
          />
          <KV
            label="Forwarded by"
            value={record.forwarded_from_service}
            mono
          />
          <KV label="Received at" value={record.received_at} mono />
        </div>
        {record.error && (
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500">
              Error
            </div>
            <div className="mt-1 rounded bg-red-50 p-2 font-mono text-[11px] text-red-700 dark:bg-red-950 dark:text-red-300">
              <div className="font-semibold">
                {record.error.name}: {record.error.message}
              </div>
              {record.error.stack && (
                <pre className="mt-1 whitespace-pre-wrap break-words text-[10px]">
                  {record.error.stack}
                </pre>
              )}
            </div>
          </div>
        )}
        {record.fields && Object.keys(record.fields).length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500">
              Fields
            </div>
            <pre className="mt-1 max-h-[300px] overflow-auto rounded bg-slate-50 p-2 font-mono text-[10px] dark:bg-slate-900">
              {JSON.stringify(record.fields, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}

function KV({
  label,
  value,
  mono,
}: {
  label: string;
  value?: string | null;
  mono?: boolean;
}) {
  if (!value) return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div
        className={`mt-0.5 truncate ${mono ? "font-mono text-[11px]" : "text-xs"}`}
      >
        {value}
      </div>
    </div>
  );
}
